/* based on https://github.com/react-bootstrap/react-bootstrap/blob/master/src/SafeAnchor.js */
import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';

import { parameterize } from '../../utils';

import './Link.scss';

function isTrivialHref(href) {
  if (typeof href === 'object') {
    return false;
  }
  return !href || href.trim() === '#';
}

function getHref(to) {
  if (to === null) {
    return '#';
  }
  if (typeof to === 'object') {
    const { pathname, query } = to;
    if (!pathname || !query) {
      return '#';
    }
    return `${pathname}?${parameterize(query)}`;
  }

  return to;
}

const propTypes = {
  as: PropTypes.string,
  className: PropTypes.string,
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  disabled: PropTypes.bool,
  role: PropTypes.string,
  variant: PropTypes.oneOf([
    'inline', // when a link is inline with text
    'article', // blog post
    'footer', // footer links
    'menu', // in top right menu
    'button-primary', // a link styled as a button
    'button-secondary', // a link styled as a button
    'gda-button-primary', // global dining access primary
    'gda-button-secondary', // global dining access secondary
    'sidenav',
  ]),

  // sizes
  sm: PropTypes.bool,
  md: PropTypes.bool,
  lg: PropTypes.bool,

  tabIndex: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
};

const defaultProps = {
  as: 'a',
  className: undefined,
  href: undefined,
  disabled: false,
  role: undefined,
  variant: undefined,
  tabIndex: 0,
  sm: false,
  md: false,
  lg: false,
};

const Link = React.forwardRef(
  (
    {
      as: Component = 'a',
      className,
      disabled,
      href,
      variant,
      sm,
      md,
      lg,
      ...props
    },
    ref
  ) => {
    if (isTrivialHref(href)) {
      props.role = props.role || 'button';
      // ensure there is an href on the node
      href = href || '#';
    }

    if (disabled) {
      props.tabIndex = -1;
      props['aria-disabled'] = true;
    }

    const classes = classNames(
      'Link',
      {
        [`Link__${variant}`]: !!variant,
      },
      sm && 'Link--sm',
      md && 'Link--md',
      lg && 'Link--lg',
      className
    );

    return (
      <Component
        ref={ref}
        className={classes}
        href={getHref(href)}
        {...props}
      />
    );
  }
);

Link.propTypes = propTypes;
Link.defaultProps = defaultProps;
Link.displayName = 'Link';
export default Link;
export { getHref };
